import {
  SPACE_MEMBER_TYPE,
  SpaceMember,
} from "@highnote/server/src/core/entities";
import { highnote } from "@highnote/server/src/sdk";
import { useEffect, useMemo, useState } from "react";
import {
  reportMembershipAttach,
  reportMembershipError,
} from "App/modules/membershipTelemetry";

/**
 * The `share` and `public` member documents of one space.
 *
 * Managers only. The security rules grant this `list` query
 * (`type in ['share','public']`) to managers only, and a Firestore `list`
 * fails as a whole: a non-manager that attaches this listener gets nothing,
 * not a filtered list. So to attach it for everyone gives no partial
 * result. It only adds a permanent contribution to the
 * `membership.listener_error` rate, and the on-call alert pages on that
 * rate. This hook therefore refuses to attach for non-managers.
 *
 * Warning: do not attach it for a non-manager "just to try".
 *
 * This deliberately overrides an earlier plan decision, which said that any
 * space reader can list the share and public documents. A later correction
 * limited the query to managers, and parts of the older plan text did not
 * catch up.
 *
 * The data is bounded in practice (one `public` document and a small number
 * of share keys), so one listener with no pagination is enough.
 */
export const useSpaceAccessMembers = ({
  spaceId,
  canManage,
  enabled,
}: {
  spaceId?: Id;
  canManage: boolean;
  enabled: boolean;
}) => {
  const [members, setMembers] = useState<SpaceMember[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled || !spaceId || !canManage) {
      setLoading(false);
      return;
    }
    setLoading(true);
    reportMembershipAttach("access");
    const unsubscribe = highnote.watchSpaceAccessMembers({
      spaceId,
      onChange: (next) => {
        setMembers(next);
        setLoading(false);
        setError(false);
      },
      onError: (e) => {
        /*
         * Do not clear `members`. The caller falls back to the legacy
         * `entity.rolesV2` map, which the migration keeps correct until a
         * later phase removes it.
         */
        setLoading(false);
        setError(true);
        reportMembershipError("access", e);
      },
    });
    return () => unsubscribe();
  }, [enabled, spaceId, canManage]);

  const shareMember = useMemo(
    () => members.find((m) => m.type === SPACE_MEMBER_TYPE.SHARE),
    [members],
  );
  const publicMember = useMemo(
    () => members.find((m) => m.type === SPACE_MEMBER_TYPE.PUBLIC),
    [members],
  );

  return { shareMember, publicMember, loading, error };
};
